import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as Marzipano from "marzipano";
import "../styles/VirtualTour.css";

export default function VirtualTour() {
  const navigate = useNavigate();

  const [rooms, setRooms] =
    useState([]);

  const [activeRoom, setActiveRoom] =
    useState(null);

  const [hotspots, setHotspots] =
    useState([]);

  const [moving, setMoving] =
    useState(false);

  const [fullScreen, setFullScreen] =
    useState(false);

  const [showMiniMap, setShowMiniMap] =
    useState(true);

  const [countDown, setCountDown] =
    useState(30);

  const screenRef =
    useRef(null);

  const viewerRef =
    useRef(null);

  const panoRef =
    useRef(null);

  const sceneRef =
    useRef(null);

  const viewRef =
    useRef(null);

  const moveTimerRef =
    useRef(null);

  const finishTimerRef =
    useRef(null);

  const rotateRef =
    useRef(null);

  const autoNextRef =
    useRef(null);

  const countRef =
    useRef(null);

  /* =========================
     LOAD ROOMS
  ========================= */
  useEffect(() => {
    loadRooms();
  }, []);

  const loadRooms =
    async () => {
      try {
        const res =
          await axios.get(
            "https://deoria.ilakshyaerp.in/api/tours"
          );

        const list =
          res.data || [];

        setRooms(list);

        if (
          list.length > 0
        ) {
          setActiveRoom(
            list[0]
          );
        }
      } catch (err) {
        console.log(err);
      }
    };

  /* =========================
     LOAD HOTSPOTS
  ========================= */
  useEffect(() => {
    if (!activeRoom)
      return;

    loadHotspots(
      activeRoom.id
    );
  }, [activeRoom]);

  const loadHotspots =
    async (id) => {
      try {
        const res =
          await axios.get(
            `https://deoria.ilakshyaerp.in/api/hotspots/${id}`
          );

        setHotspots(
          res.data || []
        );
      } catch {
        setHotspots([]);
      }
    };

  /* =========================
     CREATE VIEWER ONLY ONCE
  ========================= */
  useEffect(() => {
    if (!viewerRef.current)
      return;

    panoRef.current =
      new Marzipano.Viewer(
        viewerRef.current,
        {
          controls: {
            mouseViewMode: "drag",
            scrollZoom: true
          },
        }
      );

    return () => {
      clearIntervals();

      if (
        panoRef.current &&
        panoRef.current.destroy
      ) {
        panoRef.current.destroy();
      }

      panoRef.current =
        null;
    };
  }, []);

  /* =========================
     FULLSCREEN WATCH
  ========================= */
  useEffect(() => {
    const handler =
      () => {
        setFullScreen(
          !!document.fullscreenElement
        );
      };

    document.addEventListener(
      "fullscreenchange",
      handler
    );

    return () =>
      document.removeEventListener(
        "fullscreenchange",
        handler
      );
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (panoRef.current) {
        panoRef.current.updateSize();
      }
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () =>
      window.removeEventListener("resize", handleResize);
  }, []);

  /* =========================
     CREATE SCENE
  ========================= */
  useEffect(() => {
    if (!activeRoom || !panoRef.current) return;

    createScene();

    setTimeout(() => {
      panoRef.current?.updateSize();
    }, 100);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoom, hotspots]);

  const createScene =
    () => {
      clearIntervals();

      const source =
        Marzipano.ImageUrlSource.fromString(
          `https://deoria.ilakshyaerp.in/uploads/virtual-image/${activeRoom.image}`
        );

      const geometry =
        new Marzipano.EquirectGeometry(
          [
            {
              width: window.innerWidth <= 768
                ? 2000
                : 4000,
            },
          ]
        );

      const limiter =
        Marzipano.RectilinearView.limit.traditional(
          1400,
          (110 *
            Math.PI) /
          180
        );

      const view =
        new Marzipano.RectilinearView(
          {
            yaw: 0,
            pitch: 0,
            fov:
              Math.PI / 2,
          },
          limiter
        );

      const scene =
        panoRef.current.createScene(
          {
            source,
            geometry,
            view,
          }
        );
      scene.switchTo({
        transitionDuration: 700,
      });

      requestAnimationFrame(() => {
        panoRef.current?.updateSize();
      });

      setTimeout(() => {
        panoRef.current?.updateSize();
      }, 50);

      setTimeout(() => {
        panoRef.current?.updateSize();
      }, 300);

      setTimeout(() => {
        panoRef.current?.updateSize();
      }, 100);

      setTimeout(() => {
        panoRef.current?.updateSize();
      }, 500);

      setTimeout(() => {
        panoRef.current?.updateSize();
      }, 1000);

      setTimeout(() => {
        if (panoRef.current) {
          panoRef.current.updateSize();
        }
      }, 1000);

      setTimeout(() => {
        panoRef.current?.updateSize();
        window.dispatchEvent(new Event("resize"));
      }, 500);

      sceneRef.current =
        scene;

      viewRef.current =
        view;

      addHotspots(
        scene
      );

      startAutoRotate();
      startAutoNext();
    };

  /* =========================
     HOTSPOTS
  ========================= */
  const addHotspots =
    (scene) => {
      hotspots.forEach(
        (spot) => {
          const dir =
            spot.direction ||
            "right";

          const wrap =
            document.createElement(
              "div"
            );

          wrap.className =
            "hotspot-wrap";

          const pulse =
            document.createElement(
              "div"
            );

          pulse.className =
            "hotspot-pulse";

          const btn =
            document.createElement(
              "div"
            );

          btn.className =
            "hotspot-btn";

          btn.innerHTML =
            "➜";

          if (
            dir ===
            "left"
          ) {
            btn.style.transform =
              "rotate(180deg)";
          }

          if (
            dir ===
            "up"
          ) {
            btn.style.transform =
              "rotate(-90deg)";
          }

          if (
            dir ===
            "down"
          ) {
            btn.style.transform =
              "rotate(90deg)";
          }

          wrap.appendChild(
            pulse
          );

          wrap.appendChild(
            btn
          );

          wrap.onclick =
            () => {
              resetAuto();

              walkToRoom(
                Number(
                  spot.target_scene_id
                )
              );
            };

          const yaw =
            ((Number(
              spot.yaw
            ) -
              50) /
              50) *
            1.2;

          const pitch =
            ((50 -
              Number(
                spot.pitch
              )) /
              50) *
            0.7;

          scene
            .hotspotContainer()
            .createHotspot(
              wrap,
              {
                yaw,
                pitch,
              }
            );
        }
      );
    };

  /* =========================
     AUTO ROTATE
  ========================= */
  const startAutoRotate =
    () => {
      rotateRef.current =
        setInterval(() => {
          if (
            viewRef.current
          ) {
            const yaw =
              viewRef.current.yaw();

            viewRef.current.setYaw(
              yaw + 0.003
            );
          }
        }, 30);
    };

  /* =========================
     AUTO NEXT ROOM
  ========================= */
  const startAutoNext =
    () => {
      setCountDown(30);

      countRef.current =
        setInterval(() => {
          setCountDown(
            (prev) =>
              prev - 1
          );
        }, 1000);

      autoNextRef.current =
        setTimeout(() => {
          goNextRoom();
        }, 30000);
    };

  const goNextRoom =
    () => {
      if (
        !rooms.length ||
        !activeRoom
      )
        return;

      const currentIndex =
        rooms.findIndex(
          (r) =>
            r.id ===
            activeRoom.id
        );

      const nextIndex =
        (currentIndex +
          1) %
        rooms.length;

      walkToRoom(
        rooms[nextIndex]
          .id
      );
    };

  /* =========================
     CLEAR TIMERS
  ========================= */
  const clearIntervals =
    () => {
      clearInterval(
        rotateRef.current
      );

      clearTimeout(
        autoNextRef.current
      );

      clearInterval(
        countRef.current
      );
    };

  const resetAuto =
    () => {
      clearIntervals();
      startAutoRotate();
      startAutoNext();
    };

  /* =========================
     WALK ROOM
  ========================= */
  const walkToRoom =
    (id) => {
      const room =
        rooms.find(
          (r) =>
            Number(
              r.id
            ) ===
            Number(id)
        );

      if (!room)
        return;

      clearTimeout(
        moveTimerRef.current
      );

      clearTimeout(
        finishTimerRef.current
      );

      setMoving(true);

      moveTimerRef.current =
        setTimeout(() => {
          setActiveRoom(
            room
          );

          finishTimerRef.current =
            setTimeout(
              () =>
                setMoving(
                  false
                ),
              700
            );
        }, 200);
    };

  /* =========================
     ZOOM
  ========================= */
  const zoomIn = () => {
    resetAuto();

    if (!viewRef.current) return;

    let current = viewRef.current.fov();
    let next = current - 0.20;

    if (next < 0.6) next = 0.6;

    viewRef.current.setFov(next);
  };

  const zoomOut = () => {
    resetAuto();

    if (!viewRef.current) return;

    let current = viewRef.current.fov();
    let next = current + 0.20;

    if (next > 2.2) next = 2.2;

    viewRef.current.setFov(next);
  };
  /* =========================
     FULLSCREEN
  ========================= */
  const toggleFullScreen =
    () => {
      const el =
        screenRef.current;

      if (
        !document.fullscreenElement
      ) {
        el.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    };

  /* =========================
     JSX
  ========================= */
  return (
    <div className="tour-main">

      {/* SIDEBAR */}
      <div className="tour-sidebar">

        <h1 className="tour-logo">
          School Tour
        </h1>

        <div className="room-list">

          {rooms.map(
            (room) => (
              <button
                key={room.id}
                onClick={() => {
                  resetAuto();
                  walkToRoom(
                    room.id
                  );
                }}
                className={
                  activeRoom?.id ===
                    room.id
                    ? "room-btn active-room"
                    : "room-btn"
                }
              >
                {room.title}
              </button>
            )
          )}

        </div>
      </div>

      {/* SCREEN */}
      <div
        ref={screenRef}
        className="tour-screen"
      >

        {/* CLOSE */}
        <button
          className="tour-close"
          onClick={() =>
            navigate("/")
          }
        >
          ✕
        </button>

        {/* VIEWER */}
        <div
          ref={viewerRef}
          className="viewer-box"
          style={{
            width: "100%",
            height: "100%"
          }}
        ></div>

        {/* WALKING */}
        {moving && (
          <div className="walking-layer">
            Walking...
          </div>
        )}

        {/* TIMER */}
        <div className="auto-timer">
          Next Room in{" "}
          {countDown}s
        </div>

        {/* QUICK */}
        {showMiniMap && (
          <div className="quick-box">

            <h3>
              Quick Rooms
            </h3>

            <div className="quick-list">

              {rooms.map(
                (room) => (
                  <button
                    key={room.id}
                    onClick={() => {
                      resetAuto();
                      walkToRoom(
                        room.id
                      );
                    }}
                  >
                    {room.title}
                  </button>
                )
              )}

            </div>
          </div>
        )}

        {/* CONTROLS */}
        <div className="tour-controls">

          <button
            className="map-btn"
            onClick={() =>
              setShowMiniMap(
                !showMiniMap
              )
            }
          >
            Map
          </button>

          <button
            onClick={
              toggleFullScreen
            }
          >
            {fullScreen
              ? "Exit"
              : "Full"}
          </button>

          <button
            onClick={
              zoomOut
            }
          >
            −
          </button>

          <button
            onClick={
              zoomIn
            }
          >
            +
          </button>

        </div>

        {/* THUMBNAILS */}
        <div className="thumb-bar">

          {rooms.map(
            (room) => (
              <img
                key={room.id}
                src={`https://deoria.ilakshyaerp.in/uploads/virtual-image/${room.image}`}
                alt="room"
                onClick={() => {
                  resetAuto();
                  walkToRoom(
                    room.id
                  );
                }}
                className={
                  activeRoom?.id ===
                    room.id
                    ? "thumb active-thumb"
                    : "thumb"
                }
              />
            )
          )}

        </div>

      </div>
    </div>
  );
}